import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useLocales } from '@/locales';
import { useUserStore } from '@/stores/userStore';
import { useErrorDialogDispatch } from '@/stores/errorDialogStore';
import { useAdminMe } from '@/hooks/api/useAdmin';
import { useClients, useCallbacks } from '@/hooks/api/useCallCenter';
import Backdrop from '@/components/ui/feedback/Backdrop';

/**
 * OperatorWrapper - Initializes the call center operator view.
 * Data comes from React Query; this wrapper only gates on readiness
 * and enforces the operator role.
 */
export function OperatorWrapper({ children }) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const errorDialogDispatch = useErrorDialogDispatch();
  const removeUser = useUserStore(state => state.removeUser);

  const meQuery = useAdminMe();
  const clientsQuery = useClients();
  const callbacksQuery = useCallbacks();

  const role = meQuery.data?.users?.role;
  const wrongRole = meQuery.isSuccess && role !== 'operator';

  const ready =
    meQuery.isSuccess &&
    role === 'operator' &&
    clientsQuery.isSuccess &&
    callbacksQuery.isSuccess;

  const failed =
    meQuery.isError || clientsQuery.isError || callbacksQuery.isError;

  // Non-operators must not see the call center — drop the session
  useEffect(() => {
    if (!wrongRole) return undefined;
    removeUser();
    localStorage.removeItem('hackable');
    const timer = setTimeout(() => {
      navigate('/auth/admin');
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrongRole]);

  useEffect(() => {
    if (!failed) return;
    errorDialogDispatch({
      type: 'OPEN_ERROR_DIALOG',
      errorMsg: translate('systemError'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  if (ready) {
    return children;
  }

  return <Backdrop open={true} background />;
}

OperatorWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

// Backwards compatibility alias
export const OperatorProvider = OperatorWrapper;

export default OperatorWrapper;
